import { useState, useEffect, useCallback } from 'react';
import api from '../api/client';
import useCompanyData from './useCompanyData';
import { readWhatsAppCompanyId } from '../components/admin/pages/settings/whatsappTemplateShared';

/**
 * Resolve company id for admin settings pages.
 * Matches WhatsAppSettings fallbacks when userContext is not loaded yet.
 */
export default function useResolvedCompanyId() {
  const { companyId: fromHook, loading: hookLoading } = useCompanyData();
  const [resolvedId, setResolvedId] = useState(null);
  const [resolving, setResolving] = useState(true);

  const resolveCompanyId = useCallback(async () => {
    const stored = readWhatsAppCompanyId();
    if (stored) {
      return stored;
    }
    if (fromHook) {
      return fromHook;
    }
    try {
      const res = await api.get('/companies/');
      const companies = res.data?.results || res.data || [];
      if (Array.isArray(companies) && companies.length > 0) {
        return companies[0].id;
      }
      const token = localStorage.getItem('adminToken') || localStorage.getItem('token');
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.company_id) {
          return payload.company_id;
        }
      }
    } catch (err) {
      console.error('Failed to resolve company id', err);
    }
    return null;
  }, [fromHook]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setResolving(true);
      const id = await resolveCompanyId();
      if (!cancelled) {
        setResolvedId(id);
        setResolving(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [resolveCompanyId]);

  const stored = readWhatsAppCompanyId();

  return {
    companyId: stored || fromHook || resolvedId,
    loading: hookLoading || resolving,
  };
}
